import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Loader2, AlertTriangle, Database, ShieldAlert, Sparkles, HardHat, IndianRupee, Users, Ghost } from "lucide-react";

export default function AdminDangerZone() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [orphans, setOrphans] = useState(null);
  const [orphanConfirm, setOrphanConfirm] = useState("");

  const scanOrphans = async () => {
    setBusy(true); setError(""); setResult(null);
    try {
      const { data } = await api.get("/admin/orphans");
      setOrphans(data);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const cleanOrphans = async () => {
    if (orphanConfirm !== "CLEAN ORPHANS") {
      setError('You must type "CLEAN ORPHANS" exactly to confirm.');
      return;
    }
    setBusy(true); setError(""); setResult(null);
    try {
      const { data } = await api.post("/admin/orphans/cleanup", { confirmation: orphanConfirm });
      setResult({ _kind: "orphans", ...data });
      setOrphans([]);
      setOrphanConfirm("");
      load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch((e) => setError(formatApiError(e.response?.data?.detail) || e.message));
  };
  useEffect(() => { if (user?.role === "super_admin") load(); }, [user]);

  if (user && user.role !== "super_admin") return <Navigate to="/" replace />;

  const reset = async () => {
    setError(""); setResult(null);
    if (confirm !== "RESET DEMO DATA") {
      setError('You must type "RESET DEMO DATA" exactly to confirm.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-demo-data", { confirmation: confirm });
      setResult(data);
      setConfirm("");
      load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-page">
      <header>
        <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Super Admin</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Admin · Danger Zone</h1>
        <p className="text-stone-600 mt-1 text-sm">One-time data operations. Hidden from all roles except Super Admin.</p>
      </header>

      <div className="card-flat">
        <div className="flex items-center gap-2 mb-4">
          <Database className="w-4 h-4" style={{ color: "var(--color-primary)" }} />
          <div className="kpi-label">Database Snapshot</div>
        </div>
        {!stats ? <Loader2 className="w-5 h-5 animate-spin" /> : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="db-stats">
            {Object.entries(stats).map(([k, v]) => (
              <div key={k} className="border border-stone-100 rounded-md p-3">
                <div className="kpi-label">{k.replaceAll("_", " ")}</div>
                <div className="font-heading text-xl font-bold mt-1">{v}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card-flat" data-testid="import-master-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "#DCE9F0" }}>
            <Users className="w-5 h-5" style={{ color: "#1F4863" }} />
          </div>
          <div className="flex-1">
            <div className="font-heading text-lg font-bold" style={{ color: "#1F4863" }}>Import Employee Master (52 — Navaratnaa 36 + Sherataan 16)</div>
            <div className="text-sm text-stone-600 mt-1">
              Loads both official salary registers with Company tags: names, designations, locations, DOJ, and exact salary splits
              (Basic/HRA/Conveyance/Medical/Special), including EPF enrolment, ESI applicability (Sherataan: no ESI) and PT exemptions per employee.
              <b> Idempotent</b> — existing employees (matched by name + company) are updated, missing ones created. Emails/photos of existing employees are not touched.
            </div>
          </div>
          <button onClick={async () => {
            setBusy(true); setError(""); setResult(null);
            try {
              const { data } = await api.post("/admin/import-employee-master");
              setResult({ deleted: { created: data.created, updated: data.updated }, _kind: "import" });
              load();
            } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
            finally { setBusy(false); }
          }} disabled={busy} data-testid="import-master-button"
                  className="px-4 py-2 rounded-md text-white text-sm font-semibold flex items-center gap-2 shrink-0"
                  style={{ background: "#1F4863" }}>
            <Users className="w-4 h-4" /> Import Employees
          </button>
        </div>
      </div>

      <div className="card-flat" data-testid="sync-names-card">
        <div className="flex items-start gap-3 mb-2">
          <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "#F4F1DE" }}>
            <Users className="w-5 h-5" style={{ color: "#9A6020" }} />
          </div>
          <div className="flex-1">
            <div className="font-heading text-lg font-bold" style={{ color: "#9A6020" }}>Sync Display Names</div>
            <div className="text-sm text-stone-600 mt-1">
              Re-syncs the cached <code>employee_name</code> on Payroll, Attendance, TA/DA, Leaves, Increments,
              Loans, Exits, Performance, Dealer Receipts &amp; Candidate Offers to match each employee&apos;s current full name.
              Run this once after bulk-renaming people. New edits propagate automatically going forward.
            </div>
          </div>
          <button onClick={async () => {
            setBusy(true); setError(""); setResult(null);
            try {
              const { data } = await api.post("/admin/sync-employee-names");
              const total = Object.values(data.synced || {}).reduce((s, v) => s + v, 0);
              setResult({ _kind: "sync", total, synced: data.synced });
            } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
            finally { setBusy(false); }
          }} disabled={busy} data-testid="sync-names-button"
                  className="px-4 py-2 rounded-md text-white text-sm font-semibold flex items-center gap-2 shrink-0"
                  style={{ background: "#9A6020" }}>
            <Users className="w-4 h-4" /> Sync Names
          </button>
        </div>
      </div>

      <div className="card-flat" data-testid="orphans-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "#FEF3C7" }}>
            <Ghost className="w-5 h-5" style={{ color: "#9A6020" }} />
          </div>
          <div className="flex-1">
            <div className="font-heading text-lg font-bold" style={{ color: "#9A6020" }}>Clean Orphan Records</div>
            <div className="text-sm text-stone-600 mt-1">
              Finds Attendance / Payroll / Leaves / TA-DA rows that reference an <b>employee_id no longer in the directory</b>
              (leftover from an old hard-delete). Scan first to see the list — then confirm with <code>CLEAN ORPHANS</code>.
              Historical rows for currently-active <i>and</i> exit-marked employees are never touched.
            </div>
          </div>
          <button onClick={scanOrphans} disabled={busy} data-testid="scan-orphans-button"
                  className="px-4 py-2 rounded-md text-white text-sm font-semibold flex items-center gap-2 shrink-0"
                  style={{ background: "#9A6020" }}>
            <Ghost className="w-4 h-4" /> Scan
          </button>
        </div>
        {result?._kind === "orphans" && (
          <div className="mt-3 p-3 rounded-md text-sm bg-green-50 border border-green-200 text-green-800" data-testid="orphans-result">
            ✅ Cleaned {result.orphan_ids_cleaned} orphan reference(s). Removed rows: {Object.entries(result.deleted).filter(([_, v]) => v > 0).map(([k, v]) => `${k} (${v})`).join(", ") || "none"}.
          </div>
        )}
        {orphans !== null && (
          <div className="mt-2 space-y-3">
            {orphans.length === 0 ? (
              <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2" data-testid="orphans-empty">
                No orphan records found — your data is clean.
              </div>
            ) : (
              <>
                <div className="text-xs text-stone-600">
                  Found <b>{orphans.length}</b> orphan employee reference(s):
                </div>
                <div className="max-h-56 overflow-y-auto border border-stone-200 rounded-md divide-y divide-stone-100">
                  {orphans.map((o) => (
                    <div key={o.employee_id} className="px-3 py-2 flex items-center justify-between text-sm" data-testid={`orphan-row-${o.employee_id}`}>
                      <div>
                        <span className="font-medium text-stone-900">{o.name}</span>
                        {o.designation && <span className="text-stone-500 ml-1">· {o.designation}</span>}
                      </div>
                      <div className="text-xs text-stone-500">
                        {Object.entries(o.counts).map(([k, v]) => `${k}=${v}`).join(" · ")}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input value={orphanConfirm} onChange={(e) => setOrphanConfirm(e.target.value)}
                         placeholder='Type "CLEAN ORPHANS" to confirm' data-testid="orphans-confirm-input"
                         className="flex-1 px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:border-red-600" />
                  <button onClick={cleanOrphans} disabled={busy || orphanConfirm !== "CLEAN ORPHANS"}
                          data-testid="clean-orphans-button"
                          className="px-4 py-2 rounded-md text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                          style={{ background: "#9A3412" }}>
                    <Ghost className="w-4 h-4" /> Delete all orphans
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="card-flat" data-testid="seed-fleet-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "var(--color-primary-light)" }}>
            <Sparkles className="w-5 h-5" style={{ color: "var(--color-primary)" }} />
          </div>
          <div className="flex-1">
            <div className="font-heading text-lg font-bold" style={{ color: "var(--color-primary)" }}>Seed Sample Fleet & Workforce</div>
            <div className="text-sm text-stone-600 mt-1">
              Adds 12 sample workers (from your salary statement), 3 field staff, and 4 vehicles with insurance/fitness/PUC dates. <b>Idempotent</b> — safe to click again, only adds what&apos;s missing.
            </div>
          </div>
          <button onClick={async () => {
            setBusy(true); setError(""); setResult(null);
            try {
              const { data } = await api.post("/admin/seed-sample-fleet");
              setResult({ deleted: data.added, _kind: "seed" });
              load();
            } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
            finally { setBusy(false); }
          }} disabled={busy} data-testid="seed-fleet-button"
                  className="px-4 py-2 rounded-md text-white text-sm font-semibold flex items-center gap-2"
                  style={{ background: "var(--color-primary)" }}>
            <HardHat className="w-4 h-4" /> Seed Sample Data
          </button>
        </div>
      </div>

      <div className="card-flat" data-testid="clear-salaries-card">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "#FCE7C9" }}>
            <IndianRupee className="w-5 h-5" style={{ color: "#9A6020" }} />
          </div>
          <div className="flex-1">
            <div className="font-heading text-lg font-bold" style={{ color: "#9A6020" }}>Clear Default Salary Components</div>
            <div className="text-sm text-stone-600 mt-1">
              Resets <b>Basic / HRA / Conveyance / Medical / Special / CTC</b> to <b>0</b> for employees whose values are still the seed defaults (Basic ₹30,000 + HRA ₹12,000). Employees with custom salaries entered manually are <b>not</b> touched.
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 items-end">
          <label className="block">
            <div className="kpi-label mb-1.5">Type <span className="font-mono bg-stone-100 px-2 py-0.5 rounded text-stone-900">CLEAR DEFAULT SALARIES</span> to confirm</div>
            <input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="clear-salary-confirm"
                   placeholder="CLEAR DEFAULT SALARIES"
                   className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-orange-500 font-mono" />
          </label>
          <button onClick={async () => {
            if (confirm !== "CLEAR DEFAULT SALARIES") { setError('Type "CLEAR DEFAULT SALARIES" exactly to confirm'); return; }
            setError(""); setResult(null); setBusy(true);
            try {
              const { data } = await api.post("/admin/bulk-clear-salaries", { confirmation: confirm, only_seed_defaults: true });
              setResult({ deleted: { matched: data.matched, modified: data.modified }, _kind: "clear" });
              setConfirm(""); load();
            } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
            finally { setBusy(false); }
          }} disabled={busy || confirm !== "CLEAR DEFAULT SALARIES"} data-testid="clear-salaries-button"
                  className="px-4 py-2.5 rounded-md text-white text-sm font-semibold disabled:opacity-50"
                  style={{ background: "#9A6020" }}>
            {busy ? "Working…" : "Reset Default Salaries → 0"}
          </button>
        </div>
      </div>

      <div className="card-flat border-2" style={{ borderColor: "#E07A5F" }} data-testid="danger-zone">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "#FADED7" }}>
            <ShieldAlert className="w-5 h-5" style={{ color: "#9A3412" }} />
          </div>
          <div>
            <div className="font-heading text-lg font-bold" style={{ color: "#9A3412" }}>Reset Demo Data</div>
            <div className="text-sm text-stone-600 mt-1">
              Deletes all <b>operational</b> records: employees, attendance, leaves, payroll, TA/DA, increments,
              assets, performance, candidates, exits, loans, documents, notifications.<br />
              Keeps: users, departments, designations, locations.
            </div>
            <div className="text-xs text-red-700 mt-2 font-medium">This action cannot be undone.</div>
          </div>
        </div>

        <div className="mt-4">
          <label className="block">
            <div className="kpi-label mb-1.5">Type <span className="font-mono bg-stone-100 px-2 py-0.5 rounded text-stone-900">RESET DEMO DATA</span> to confirm</div>
            <input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input"
                   placeholder="RESET DEMO DATA"
                   className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-red-500 font-mono" />
          </label>
          {error && <div className="text-sm px-3 py-2 mt-3 rounded-md flex items-center gap-2"
                          style={{ background: "#FADED7", color: "#9A3412" }} data-testid="reset-error">
            <AlertTriangle className="w-4 h-4" /> {error}
          </div>}
          {result && result._kind !== "orphans" && (
            <div className="mt-3 p-3 rounded-md text-sm" style={{ background: "var(--color-primary-light)", color: "var(--color-primary)" }} data-testid="reset-result">
              ✅ Reset complete. Deleted: {Object.entries(result.deleted).filter(([_, v]) => v > 0).map(([k, v]) => `${k} (${v})`).join(", ") || "nothing — was already empty"}.
            </div>
          )}
          <button onClick={reset} disabled={busy || confirm !== "RESET DEMO DATA"}
                  data-testid="reset-demo-button"
                  className="mt-4 px-5 py-2.5 rounded-md text-white font-semibold flex items-center gap-2 disabled:opacity-50"
                  style={{ background: "#9A3412" }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
            Permanently Delete Demo Data
          </button>
        </div>
      </div>
    </div>
  );
}
